import React from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Card, CardContent, Checkbox, Chip, Divider, Grid, IconButton, MenuItem, Stack, Typography } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import T from 'T'
import { HHFormNumberField } from 'components/form-fields/v5'
import { ArrowDropDown, ArrowDropUp, ChevronLeft } from '@mui/icons-material'
import InputAdornment from '@mui/material/InputAdornment'
import useCreateCSFilterOptions from 'components/configured-service/CreateDraftConfiguredServiceDialog/useCreateCSFilterOptions'
import { Z_INDEX } from 'theme/zIndex'
import { shallowEqual, useSelector } from 'react-redux'
import { selectCSDCurrentTab } from 'slices/configuredService/createDraftCSDSlice'
import MethodChip from 'components/common/chip/MethodChip'
import ServiceActionTypeChip from 'components/services/ServiceActionTypeChip'
import ServiceMaterialTypeChip from 'components/services/ServiceMaterialTypeChip'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import BusinessLineChip from 'components/business-line/BusinessLineChip'
import { filterDefaultValues } from './filterSettings'
import { FilterMultiSelectField, RecurrenceFilterToggleButtonGroup, FilterSingleSelectField } from './filters'
import { PRICED_SERVICE_TYPE } from './settings'
import useActiveFilterCount from './hooks/useActiveFilterCount'
import { useServiceListFiltersEffects } from './hooks/useServiceListFiltersEffects'

const slotPropsPopper = {
  popper: {
    sx: {
      zIndex: Z_INDEX.MENU,
    },
  },
}

const ServiceListFilterSection = ({ onFilterChange, control, rows = [], filterByRows = false, onToggle, disableOutline = false }) => {
  const { getValues, setValue } = useFormContext()

  const recurrenceType = useWatch({ control, name: 'serviceList.recurrenceType', defaultValue: filterDefaultValues.recurrenceType })
  const measure = useWatch({ control, name: 'serviceList.measure', defaultValue: filterDefaultValues.measure })
  const material = useWatch({ control, name: 'serviceList.material', defaultValue: filterDefaultValues.material })
  const action = useWatch({ control, name: 'serviceList.action', defaultValue: filterDefaultValues.action })
  const businessLine = useWatch({ control, name: 'serviceList.businessLine', defaultValue: filterDefaultValues.businessLine })
  const recurrenceFrequency = useWatch({
    control,
    name: 'serviceList.recurrenceFrequency',
    defaultValue: filterDefaultValues.recurrenceFrequency,
  })
  const recurrenceOption = useWatch({ control, name: 'serviceList.recurrenceOption', defaultValue: filterDefaultValues.recurrenceOption })
  const method = useWatch({ control, name: 'serviceList.method', defaultValue: filterDefaultValues.method })
  const pricingPeriod = useWatch({ control, name: 'serviceList.pricingPeriod', defaultValue: filterDefaultValues.pricingPeriod })
  const pricingZone = useWatch({ control, name: 'serviceList.pricingZone', defaultValue: filterDefaultValues.pricingZone })
  const isRecurring = recurrenceType === PRICED_SERVICE_TYPE.RECURRING
  const currentTab = useSelector(selectCSDCurrentTab, shallowEqual)

  const {
    businessLineOptions,
    materialOptions,
    measureOptions,
    methodOptions,
    serviceActionOptions,
    recurrenceOptions,
    pricingPeriodOptions,
    pricingZoneOptions,
  } = useCreateCSFilterOptions({ rows, filterByRows })

  const activeFilterCount = useActiveFilterCount(control)

  const handleClearAll = () => {
    setValue('serviceList.serviceType', filterDefaultValues.serviceType)
    setValue('serviceList.recurrenceType', filterDefaultValues.recurrenceType)
    setValue('serviceList.measure', filterDefaultValues.measure)
    setValue('serviceList.material', filterDefaultValues.material)
    setValue('serviceList.action', filterDefaultValues.action)
    setValue('serviceList.method', filterDefaultValues.method)
    setValue('serviceList.businessLine', filterDefaultValues.businessLine)
    setValue('serviceList.recurrenceFrequency', filterDefaultValues.recurrenceFrequency)
    setValue('serviceList.recurrenceOption', filterDefaultValues.recurrenceOption)
    setValue('serviceList.pricingPeriod', filterDefaultValues.pricingPeriod)
    setValue('serviceList.pricingZone', filterDefaultValues.pricingZone)

    onFilterChange({
      items: [],
      quickFilterValues: [],
    })
  }

  useServiceListFiltersEffects({
    setValue,
    onFilterChange,
    recurrenceType,
    recurrenceFrequency,
    recurrenceOption,
    measure,
    material,
    action,
    method,
    businessLine,
    pricingPeriod,
    pricingZone,
    isRecurring,
    currentTab,
  })

  return (
    <Card variant={disableOutline ? 'elevation' : 'outlined'} elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <CardContent sx={{ flex: 1, overflow: 'auto', ...(disableOutline && { pl: 0, pr: 2 }) }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography color="textSecondary" variant="h5">
              Filter options
            </Typography>
            {activeFilterCount > 0 && <Chip label={activeFilterCount} size="small" color="primary" sx={{ minWidth: '1.5rem', '& .MuiChip-label': { px: 0 } }} />}
          </Stack>
          <Stack direction="row" alignItems="center">
            <Button onClick={handleClearAll}>{T.CLEAR_ALL}</Button>
            {onToggle && (
              <IconButton size="small" onClick={onToggle} aria-label="Collapse filter options" title="Collapse filter options">
                <ChevronLeft fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Box pb={2} pt={1}>
          <Divider />
        </Box>
        <Grid container spacing={2}>
          <Grid size={12}>
            <RecurrenceFilterToggleButtonGroup
              name="serviceList.recurrenceType"
              control={control}
              defaultValue={filterDefaultValues.recurrenceType}
            />
          </Grid>

          {isRecurring && (
            <Grid size={12} container spacing={2} sx={{ mt: 1 }}>
              <Grid size="auto">
                <HHFormNumberField
                  allowNegative={false}
                  sx={{
                    width: '5rem',
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Stack
                          direction="column"
                          sx={{
                            height: '50%',
                          }}
                        >
                          <IconButton
                            sx={{
                              height: '24px',
                              width: '24px',
                            }}
                            size="small"
                            onClick={() => {
                              const currentValue = getValues('serviceList.recurrenceFrequency')

                              if (currentValue >= 8) return
                              const newValue = !currentValue ? 1 : Math.max(1, Number(currentValue) + 1)
                              setValue('serviceList.recurrenceFrequency', newValue)
                            }}
                          >
                            <ArrowDropUp />
                          </IconButton>
                          <IconButton
                            sx={{
                              height: '24px',
                              width: '24px',
                            }}
                            size="small"
                            onClick={() => {
                              const currentValue = getValues('serviceList.recurrenceFrequency')
                              if (currentValue <= 1) {
                                setValue('serviceList.recurrenceFrequency', '')
                              } else {
                                const newValue = !currentValue ? 1 : Math.max(1, currentValue - 1)
                                setValue('serviceList.recurrenceFrequency', newValue)
                              }
                            }}
                          >
                            <ArrowDropDown />
                          </IconButton>
                        </Stack>
                      </InputAdornment>
                    ),
                  }}
                  defaultSize
                  suffix="x"
                  name="serviceList.recurrenceFrequency"
                  control={control}
                  deprecatedLabel={false}
                />
              </Grid>

              <Grid size="grow">
                <FilterSingleSelectField
                  deprecatedLabel={false}
                  name="serviceList.recurrenceOption"
                  control={control}
                  label={T.REPEATS}
                  options={recurrenceOptions}
                  getOptionLabel={option => option.label}
                  fullWidth
                  slotProps={slotPropsPopper}
                />
              </Grid>
            </Grid>
          )}
          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="serviceList.businessLine"
              control={control}
              label="Business line"
              options={businessLineOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
              renderOption={({ className, ...optionProps }, option, { selected }) => (
                <MenuItem key={`business-line-option-${option.value}`} dense sx={{ paddingX: 0.5, paddingY: 0.25 }} {...optionProps}>
                  <Checkbox checked={selected} />
                  <BusinessLineChip size="small" columnSpacing={0.5} direction="row" businessLineName={option.label} businessLineType={option.businessLineType} />
                </MenuItem>
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => {
                  const { key, ...itemProps } = getItemProps({ index })
                  return (
                    <BusinessLineChip
                      key={key}
                      size="small"
                      columnSpacing={0.5}
                      direction="row"
                      businessLineName={option.label}
                      businessLineType={option.businessLineType}
                      {...itemProps}
                    />
                  )
                })
              }
            />
          </Grid>
          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="serviceList.pricingPeriod"
              control={control}
              label="Pricing period"
              options={pricingPeriodOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
            />
          </Grid>

          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="serviceList.pricingZone"
              control={control}
              label="Pricing zone"
              options={pricingZoneOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
            />
          </Grid>
          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="serviceList.measure"
              control={control}
              label="Measure"
              options={measureOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
              renderOption={({ className, ...optionProps }, option, { selected }) => (
                <MenuItem key={`measure-option-${option.value}`} dense sx={{ paddingX: 0.5, paddingY: 0.25 }} {...optionProps}>
                  <Checkbox checked={selected} />
                  <MeasureChip size="small" measureUnit={option.unit} label={option.label} />
                </MenuItem>
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => {
                  const { key, ...itemProps } = getItemProps({ index })
                  return <MeasureChip key={key} size="small" measureUnit={option.unit} label={option.label} {...itemProps} />
                })
              }
            />
          </Grid>

          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="serviceList.material"
              control={control}
              label="Material"
              options={materialOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
              renderOption={({ className, ...optionProps }, option, { selected }) => (
                <MenuItem key={`material-option-${option.value}`} dense sx={{ paddingX: 0.5, paddingY: 0.25 }} {...optionProps}>
                  <Checkbox checked={selected} />
                  <ServiceMaterialTypeChip size="small" label={option.label} materialColor={option.color} display="inline-flex" />
                </MenuItem>
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => {
                  const { key, ...itemProps } = getItemProps({ index })
                  return (
                    <ServiceMaterialTypeChip key={key} size="small" label={option.label} materialColor={option.color} display="inline-flex" {...itemProps} />
                  )
                })
              }
            />
          </Grid>

          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="serviceList.action"
              control={control}
              label="Action"
              options={serviceActionOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
              renderOption={({ className, ...optionProps }, option, { selected }) => (
                <MenuItem key={`action-option-${option.value}`} dense sx={{ paddingX: 0.5, paddingY: 0.25 }} {...optionProps}>
                  <Checkbox checked={selected} />
                  <ServiceActionTypeChip action={option.value} size="small" />
                </MenuItem>
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => {
                  const { key, ...itemProps } = getItemProps({ index })
                  return <ServiceActionTypeChip key={key} action={option.value} size="small" {...itemProps} />
                })
              }
            />
          </Grid>

          <Grid size={12}>
            <FilterMultiSelectField
              deprecatedLabel={false}
              name="serviceList.method"
              control={control}
              label="Method"
              options={methodOptions}
              getOptionLabel={option => option.label}
              fullWidth
              slotProps={slotPropsPopper}
              renderOption={({ className, ...optionProps }, option, { selected }) => (
                <MenuItem key={`method-option-${option.value}`} dense sx={{ paddingX: 0.5, paddingY: 0.25 }} {...optionProps}>
                  <Checkbox checked={selected} />
                  <MethodChip size="small" methodType={option.label} />
                </MenuItem>
              )}
              renderValue={(value, getItemProps) =>
                value.map((option, index) => {
                  const { key, ...itemProps } = getItemProps({ index })
                  return <MethodChip key={key} size="small" methodType={option.label} {...itemProps} />
                })
              }
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}

ServiceListFilterSection.propTypes = {
  onFilterChange: PropTypes.func,
  control: PropTypes.object.isRequired,
  rows: PropTypes.array,
  filterByRows: PropTypes.bool,
  onToggle: PropTypes.func,
  disableOutline: PropTypes.bool,
}

export default ServiceListFilterSection
